import React from 'react';
import { useHistory } from 'react-router-dom';
import { card, Notification } from 'components/common';
import { Form } from 'antd';
import * as dal from 'dal';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import ROUTES from 'utils/constants/routes.json';
import { Item } from 'components/common/form';
import { Input } from 'components/common/input';
import { PrimaryButton } from 'components/common/button';
import { Title } from 'components/common/text';
import styled from 'styled-components';
import { STYLES } from 'utils/constants/styles';
import { isPossiblyPhoneNumber } from 'utils/helpers/phoneValidation';

const StyledForm = styled(Form)`
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: center;
    width: 100%;
`;

const StyledContainer = styled.div`
    display: flex;
    justify-content: center;
    padding-top: ${STYLES.CONTENT_PADDING_TOP};
`;

const { LogoCard } = card;
const { openNotificationWithIcon } = Notification;

const ResetPasswordView: React.FC = () => {
    const history = useHistory();
    const { t, i18n } = useTranslation(['passwordManagement']);
    const [form] = Form.useForm();

    const onFinish = (e: any) => {
        dal.auth
            .resetPassword(e, i18n.language)
            .then(() => history.push(ROUTES.reset_password_link_sent))
            .catch((err: AxiosError) => {
                openNotificationWithIcon({ type: 'error', message: err.response?.data });
            });
    };

    const validateOneOfTwoRequired = (value: string, value2: string, message: string) =>
        !value && !value2 ? Promise.reject(message) : Promise.resolve();

    return (
        <StyledContainer>
            <LogoCard>
                <StyledForm
                    form={form}
                    name="basic"
                    layout="vertical"
                    initialValues={{ remember: true }}
                    onFinish={onFinish}
                    onFinishFailed={() => {}}>
                    <Title level={2}>{t('forgot_password')}</Title>
                    <Item
                        style={{ width: '75%' }}
                        label={t('email')}
                        name="email"
                        hasFeedback
                        dependencies={['phoneNumber']}
                        rules={[
                            {
                                type: 'email',
                                message: t('passwordManagement:invalid_email'),
                            },
                            {
                                validator: (_, value) =>
                                    validateOneOfTwoRequired(
                                        value,
                                        form.getFieldValue('phoneNumber'),
                                        t('email_or_phone_required')
                                    ),
                            },
                        ]}>
                        <Input placeholder="Email..." />
                    </Item>
                    <Title level={3}>{t('or')}</Title>
                    <Item
                        style={{ width: '75%' }}
                        label={t('phone_number')}
                        name="phoneNumber"
                        hasFeedback
                        dependencies={['email']}
                        rules={[
                            {
                                validator: (_, value) =>
                                    !value || isPossiblyPhoneNumber(value)
                                        ? Promise.resolve()
                                        : Promise.reject(t('invalid_phone')),
                                message: t('passwordManagement:invalid_phone'),
                            },
                            {
                                validator: (_, value) =>
                                    validateOneOfTwoRequired(
                                        value,
                                        form.getFieldValue('email'),
                                        t('email_or_phone_required')
                                    ),
                            },
                        ]}>
                        <Input placeholder={`${t('phone_number')}...`} />
                    </Item>
                    <Item style={{ width: '100%' }}>
                        <PrimaryButton style={{ width: '100%' }} htmlType="submit">
                            {t('passwordManagement:retrieve_password')}
                        </PrimaryButton>
                    </Item>
                </StyledForm>
            </LogoCard>
        </StyledContainer>
    );
};

export default ResetPasswordView;
